import React, { useState } from 'react';
import { Calculator, DollarSign, Home, Car, Zap, ShoppingCart, TrendingDown, Share2, Download, Check, MapPin } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

// Real cost data for comparison cities (monthly estimates)
// Sources: Cornwall Electric, Hydro-Quebec, OEB, Hydro One, Energir, Enbridge
const cityData = {
  toronto: {
    name: "Toronto",
    housing: 2800,
    utilities: 180,  // Toronto Hydro ~13-15¢/kWh + delivery + Enbridge gas
    groceries: 450,
    transportation: 160,
    dining: 300,
    childcare: 1800,
  },
  ottawa: {
    name: "Ottawa",
    housing: 2100,
    utilities: 175,  // Hydro Ottawa similar to Toronto + Enbridge gas
    groceries: 420,
    transportation: 130,
    dining: 250,
    childcare: 1500,
  },
  montreal: {
    name: "Montreal",
    housing: 1800,
    utilities: 115,  // Hydro-Quebec ~8¢/kWh (lowest in North America) + Energir gas
    groceries: 380,
    transportation: 100,
    dining: 220,
    childcare: 1200,
  },
  kingston: {
    name: "Kingston",
    housing: 1900,
    utilities: 175,  // Utilities Kingston/Hydro One areas + gas
    groceries: 400,
    transportation: 120,
    dining: 230,
    childcare: 1400,
  },
};

// Cornwall costs - Cornwall Electric has Hydro-Quebec contract = lower rates than rest of Ontario
// Cornwall Electric all-in rates ~14-18¢/kWh vs Hydro One ~17-21¢/kWh effective
// Source: cornwallelectric.com, choosecornwall.ca/business/electrical-advantages
const cornwallCosts = {
  housing: 1450,  // Updated to reflect current market ($377K avg home)
  utilities: 135,  // Cornwall Electric ~15-20% lower than Ontario avg due to HQ contract
  groceries: 350,
  transportation: 90,
  dining: 180,
  childcare: 950,
};

const costCategories = [
  { key: 'housing', label: 'Housing (Rent/Mortgage)', icon: Home, description: '2BR apartment or equivalent mortgage' },
  { key: 'utilities', label: 'Utilities', icon: Zap, description: 'Electricity, gas, water, internet' },
  { key: 'groceries', label: 'Groceries', icon: ShoppingCart, description: 'Monthly food & household items' },
  { key: 'transportation', label: 'Transportation', icon: Car, description: 'Gas, transit, parking' },
  { key: 'dining', label: 'Dining Out', icon: DollarSign, description: 'Restaurants & takeout' },
  { key: 'childcare', label: 'Childcare', icon: Home, description: 'Daycare (optional)' },
];

export const CostCalculatorPage = () => {
  const { toast } = useToast();
  const [selectedCity, setSelectedCity] = useState(null);
  const [includeChildcare, setIncludeChildcare] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const calculateSavings = () => {
    if (!selectedCity) return null;

    const cityInfo = cityData[selectedCity];
    let cityCosts = 0;
    let cornwallTotal = 0;

    costCategories.forEach(cat => {
      if (cat.key === 'childcare' && !includeChildcare) return;
      cityCosts += cityInfo[cat.key];
      cornwallTotal += cornwallCosts[cat.key];
    });

    const monthlySavings = cityCosts - cornwallTotal;
    const annualSavings = monthlySavings * 12;
    const percentSavings = Math.round((monthlySavings / cityCosts) * 100);

    return {
      cityName: cityInfo.name,
      cityCosts,
      cornwallTotal,
      monthlySavings,
      annualSavings,
      percentSavings,
    };
  };

  const handleCalculate = () => {
    if (!selectedCity) {
      toast({
        title: "Select a City",
        description: "Please select your current city to compare costs.",
        variant: "destructive"
      });
      return;
    }
    setShowResults(true);
  };

  const handleShare = async () => {
    const savings = calculateSavings();
    if (!savings) return;

    const shareText = `I could save $${savings.annualSavings.toLocaleString()}/year by moving to Cornwall, Ontario! That's ${savings.percentSavings}% less than ${savings.cityName}. Check your savings: `;
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Cornwall Savings',
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText + shareUrl);
      toast({
        title: "Link Copied!",
        description: "Share link copied to clipboard.",
      });
    }
  };

  const savings = calculateSavings();

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-emerald-50 min-h-screen" data-testid="cost-calculator-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full mb-4">
            <Calculator className="w-5 h-5 text-emerald-700" />
            <span className="text-sm font-semibold text-emerald-700">Cost Calculator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            How Much Could You Save?
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Compare your current city's cost of living with Cornwall, Ontario and discover your potential savings
          </p>
        </div>

        {/* City Selection */}
        <Card className="border-2 border-emerald-200 mb-8">
          <CardHeader>
            <CardTitle className="text-xl">Step 1: Select Your Current City</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(cityData).map(([key, city]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedCity(key);
                    setShowResults(false);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    selectedCity === key
                      ? 'border-emerald-500 bg-emerald-50 shadow-md'
                      : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                  }`}
                  data-testid={`city-${key}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className={`w-5 h-5 ${selectedCity === key ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className={`font-bold ${selectedCity === key ? 'text-emerald-700' : 'text-slate-900'}`}>
                      {city.name}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Avg. ${city.housing.toLocaleString()}/mo housing
                  </div>
                </button>
              ))}
            </div>

            {/* Childcare Toggle */}
            <div className="mt-6 flex items-center gap-3">
              <input
                type="checkbox"
                id="childcare"
                checked={includeChildcare}
                onChange={(e) => {
                  setIncludeChildcare(e.target.checked);
                  setShowResults(false);
                }}
                className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <label htmlFor="childcare" className="text-slate-700">
                Include childcare costs in comparison
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Calculate Button */}
        {!showResults && (
          <div className="text-center mb-8">
            <Button
              onClick={handleCalculate}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-6 text-lg shadow-lg"
              data-testid="calculate-btn"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate My Savings
            </Button>
          </div>
        )}

        {/* Results */}
        {showResults && savings && (
          <div className="space-y-8 animate-in fade-in duration-500">
            {/* Savings Summary */}
            <Card className="border-4 border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
              <CardContent className="pt-8 pb-8">
                <div className="text-center space-y-4">
                  <Badge className="bg-emerald-600 text-white text-sm px-4 py-1">
                    Your Potential Savings
                  </Badge>
                  <div className="space-y-2">
                    <div className="text-6xl sm:text-7xl font-bold text-emerald-600">
                      ${savings.annualSavings.toLocaleString()}
                    </div>
                    <div className="text-xl text-slate-600">per year</div>
                  </div>
                  <div className="flex justify-center gap-6 text-lg">
                    <div>
                      <span className="font-bold text-emerald-600">${savings.monthlySavings.toLocaleString()}</span>
                      <span className="text-slate-600"> /month</span>
                    </div>
                    <div>
                      <span className="font-bold text-emerald-600">{savings.percentSavings}%</span>
                      <span className="text-slate-600"> less than {savings.cityName}</span>
                    </div>
                  </div>
                  
                  {/* Share Button */}
                  <div className="pt-4">
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                      data-testid="share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Your Savings
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Comparison */}
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-xl">Cost Breakdown: {savings.cityName} vs Cornwall</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {costCategories.map((cat) => {
                    if (cat.key === 'childcare' && !includeChildcare) return null;
                    const cityValue = cityData[selectedCity][cat.key];
                    const cornwallValue = cornwallCosts[cat.key];
                    const diff = cityValue - cornwallValue;
                    const IconComponent = cat.icon;

                    return (
                      <div key={cat.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{cat.label}</div>
                            <div className="text-xs text-slate-500">{cat.description}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div>
                            <div className="text-sm text-slate-500">{savings.cityName}</div>
                            <div className="font-medium">${cityValue.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-500">Cornwall</div>
                            <div className="font-medium text-emerald-600">${cornwallValue.toLocaleString()}</div>
                          </div>
                          <div className="w-24">
                            <Badge className="bg-green-100 text-green-700">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              ${diff.toLocaleString()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div className="mt-6 p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-lg text-slate-900">Monthly Totals</div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-sm text-slate-500">{savings.cityName}</div>
                        <div className="font-bold text-lg">${savings.cityCosts.toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-slate-500">Cornwall</div>
                        <div className="font-bold text-lg text-emerald-600">${savings.cornwallTotal.toLocaleString()}</div>
                      </div>
                      <div className="w-32 text-right">
                        <div className="text-sm text-slate-500">You Save</div>
                        <div className="font-bold text-lg text-green-600">${savings.monthlySavings.toLocaleString()}/mo</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What Could You Do */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-xl">What Could You Do With ${savings.annualSavings.toLocaleString()}/year?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {Math.floor(savings.annualSavings / 200)} nights
                    </div>
                    <div className="text-sm text-slate-600">Weekend getaways to Montreal or Ottawa</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      ${Math.floor(savings.annualSavings / 12).toLocaleString()}/mo
                    </div>
                    <div className="text-sm text-slate-600">Extra savings or investments</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {Math.floor(savings.annualSavings / 50)} dinners
                    </div>
                    <div className="text-sm text-slate-600">Family dinners out per year</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quebec Tax Savings - Only show for Montreal */}
            {selectedCity === 'montreal' && (
              <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                    Additional Tax Savings: Quebec → Ontario
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-slate-700">
                      Moving from Quebec to Ontario means <strong>significantly lower provincial income taxes</strong>. 
                      Quebec has the highest provincial tax rates in Canada.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
                        <div className="text-3xl font-bold text-purple-600 mb-2">8-12%</div>
                        <div className="text-sm text-slate-600">
                          Estimated income tax savings for typical household incomes ($50K-$100K)
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
                        <div className="text-3xl font-bold text-purple-600 mb-2">$3,000-$8,000+</div>
                        <div className="text-sm text-slate-600">
                          Potential annual tax savings depending on income level
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-100 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-2">Provincial Tax Rate Comparison</h4>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="font-medium text-slate-600">Income</div>
                        <div className="font-medium text-slate-600">Quebec</div>
                        <div className="font-medium text-slate-600">Ontario</div>
                        <div>Up to $53K</div>
                        <div className="text-red-600 font-semibold">14%</div>
                        <div className="text-green-600 font-semibold">5.05%</div>
                        <div>$53K - $106K</div>
                        <div className="text-red-600 font-semibold">19%</div>
                        <div className="text-green-600 font-semibold">9.15%</div>
                        <div>$106K+</div>
                        <div className="text-red-600 font-semibold">24-25.75%</div>
                        <div className="text-green-600 font-semibold">11-13%</div>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <Button asChild className="bg-purple-600 hover:bg-purple-700 text-white">
                        <a href="/salary-comparison">
                          <DollarSign className="w-4 h-4 mr-2" />
                          Calculate Your Take-Home Pay
                        </a>
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-4">
                      * Tax savings are in addition to cost of living savings shown above. Actual savings depend on individual circumstances. 
                      Sources: Canada Revenue Agency, Quebec Revenue Agency (2024-2025 rates).
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="pt-8 pb-8 text-center space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Ready to Start Saving?</h3>
                <p className="text-slate-600 max-w-xl mx-auto">
                  Explore Cornwall's neighborhoods, housing options, and community resources to plan your move.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <a href="/housing-market">View Housing Market</a>
                  </Button>
                  <Button asChild variant="outline" className="border-emerald-400 text-emerald-700">
                    <a href="/explore">Explore Cornwall</a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recalculate */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={() => setShowResults(false)}
                className="text-slate-600"
              >
                ← Try a different city
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-8">
          * Cost estimates are based on average data for 2024-2025 and may vary based on individual lifestyle and choices. 
          Housing costs assume a 2-bedroom apartment or equivalent mortgage payment.
        </p>
      </div>
    </section>
  );
};
